import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import CardDropdown from 'components/common/CardDropdown';
import SoftBadge from 'components/common/SoftBadge';
import React, { useEffect, useReducer, useState } from 'react';
import { Card, Dropdown } from 'react-bootstrap';
import serviceUrl from '../../../services/serviceUrlList';
import LoadingWrapper from 'components/loading/LoadingWrapper';
import ConfirmationModal from 'components/modal/ConfirmationModal';
import { toast } from 'react-toastify';
import useDelete from 'hooks/useDelete';
import { Link, useNavigate } from 'react-router-dom';
import GlossaryTableFilter from './module/GlossaryTableFilter';
import TableHeader from 'components/common/TableHeader';
import { useQuery } from 'react-query';
import { getGlossaries } from 'services/content';
import { queryReducer } from 'reducers/queryReducer';

const Glossary = () => {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const [confirmText, setConfirmText] = useState({
    title: '',
    description: '',
    type: ''
  });
  const [queryObj, setQueryObj] = useState({
    limit: 10,
    offset: 0,
    site: 1
  });
  const { data, isLoading, refetch } = useQuery(
    ['getGlossaries', queryObj],
    () => getGlossaries(queryObj),
    {
      keepPreviousData: false,
      staleTime: Infinity
    }
  );
  useEffect(() => {
    refetch();
  }, []);

  const [deleteResponse, deleteRequest] = useDelete({
    url: serviceUrl.content.glossary.detail + selectedRowId
  });

  const handleDeleteItem = id => {
    setSelectedRowId(id);
    setConfirmText({
      title: 'Delete Keyword',
      description: 'Are you sure? Selected keyword will be deleted!',
      type: 'delete'
    });
    setShowConfirm(true);
  };

  useEffect(() => {
    if (deleteResponse.data === null && deleteResponse.error === null) return;
    refetch();
    setShowConfirm(false);
    toast.success(`Keyword deleted`, {
      theme: 'colored'
    });
    setSelectedRowId(null);
  }, [deleteResponse]);

  const handleCharFilter = filterValue => {
    const temp = { ...queryObj };
    delete temp.name__regex;
    delete temp.name__istartswith;

    if (filterValue === '#') {
      temp.name__regex = `^\\d`;
    } else if (filterValue) {
      temp.name__istartswith = filterValue;
    }
    setQueryObj(temp);
  };

  // ss pagination start
  const [{ pageIndex, pageSize, totalCount }, dispatch] = useReducer(
    queryReducer,
    { pageIndex: 0, pageSize: 10, totalCount: data?.data.count }
  );
  useEffect(() => {
    setQueryObj({
      ...queryObj,
      limit: pageSize,
      offset: pageSize * pageIndex
    });
  }, [pageSize, pageIndex, totalCount]);

  useEffect(() => {
    if (!data?.data.count) return;
    dispatch({
      type: 'TOTAL_COUNT_CHANGED',
      payload: data?.data.count
    });
  }, [data?.data.count]);
  // ss pagination end

  const columns = [
    {
      accessor: 'name',
      Header: 'Name',
      headerProps: { className: 'text-start' },
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { name, id } = rowData.row.original;
        return <Link to={`/cms/glossary-update/${id}`}>{name}</Link>;
      }
    },
    {
      accessor: 'description',
      Header: 'Description',
      headerProps: { className: 'pe-1 text-start' },
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { description } = rowData.row.original;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${description
                .replace(/<\/?[^>]+(>|$)/g, ' ')
                .slice(0, 80)} ${description.length >= 80 ? '...' : ''}`
            }}
          ></div>
        );
      }
    },
    {
      accessor: 'is_active',
      Header: 'Status',
      cellProps: { className: 'text-center' },
      Cell: rowData => {
        const { is_active } = rowData.row.original;
        if (is_active) {
          return (
            <SoftBadge pill bg="success" className="d-block">
              Active
            </SoftBadge>
          );
        }
        return (
          <SoftBadge pill bg="danger" className="d-block">
            Passive
          </SoftBadge>
        );
      }
    },
    {
      accessor: 'none',
      Header: 'Actions',
      disableSortBy: true,
      headerProps: { className: 'text-end' },
      cellProps: {
        className: 'text-end'
      },
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <CardDropdown iconClassName="fs--1">
            <div className="py-2">
              <Dropdown.Item
                onClick={() => navigate(`/cms/glossary-update/${id}`)}
              >
                Edit
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDeleteItem(id)}>
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      }
    }
  ];

  const handleNew = () => {
    navigate('/cms/glossary-add');
  };

  return (
    <div className="p-2">
      <LoadingWrapper loading={isLoading}>
        <AdvanceTableWrapper
          columns={columns}
          data={data?.data.results || []}
          selection
          sortable
          pagination
          perPage={queryObj.limit}
          queryPageIndex={pageIndex}
          totalCount={totalCount}
          dispatch={dispatch}
        >
          <Card className="mb-3">
            <Card.Header>
              <TableHeader
                title="Glossary"
                handleRefetch={refetch}
                table
                handleNew={handleNew}
                bulkTable={'Glossary'}
                bulkService="content"
              />
              <GlossaryTableFilter charFilterValue={handleCharFilter} />
            </Card.Header>
            <Card.Body className="p-0">
              <AdvanceTable
                table
                headerClassName="bg-200 text-900 text-nowrap align-middle text-center"
                tableProps={{
                  size: 'sm',
                  striped: true,
                  className: 'fs--1 mb-0'
                }}
                rowClassName="align-middle"
              />
            </Card.Body>
            <Card.Footer>
              <AdvanceTablePagination table />
            </Card.Footer>
          </Card>
        </AdvanceTableWrapper>
      </LoadingWrapper>

      <ConfirmationModal
        showConfirm={showConfirm}
        setShowConfirm={setShowConfirm}
        title={confirmText.title}
        description={confirmText.description}
        request={deleteRequest}
        type={confirmText.type}
      />
    </div>
  );
};

export default Glossary;
